import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { AdmissionEnquiry, AdmissionInterviewSlot, AdmissionStage } from '../models/admissions.model';
import { AdmissionsService } from '../services/admissions.service';
import { LocalStorageService } from '../services/local-storage.service';
import { TenantService } from '../services/tenant.service';
import { CLASS_GROUP_OPTIONS } from '../school-constants';

const STAGE_LABEL: Record<AdmissionStage, string> = {
  ENQUIRY: 'Enquiry',
  APPLICATION: 'Application',
  ASSESSMENT: 'Assessment',
  OFFERED: 'Offer',
  ENROLLED: 'Enrolled',
  WITHDRAWN: 'Withdrawn'
};

@Component({
  selector: 'app-admissions',
  templateUrl: './admissions.component.html',
  styleUrls: ['./admissions.component.css']
})
export class AdmissionsComponent implements OnInit {

  readonly classOptions = [...CLASS_GROUP_OPTIONS];
  readonly stageLabels = STAGE_LABEL;
  readonly stages: AdmissionStage[] = [
    'ENQUIRY', 'APPLICATION', 'ASSESSMENT', 'OFFERED', 'ENROLLED', 'WITHDRAWN'
  ];

  isAdmin = false;
  schoolName = '';
  loading = true;

  enquiries: AdmissionEnquiry[] = [];
  enquiryColumns = ['createdAt', 'child', 'class', 'parent', 'stage', 'actions'];
  enquiryData = new MatTableDataSource<AdmissionEnquiry>([]);

  interviews: AdmissionInterviewSlot[] = [];
  interviewData = new MatTableDataSource<AdmissionInterviewSlot>([]);
  interviewColumns = ['scheduledAt', 'enquiryId', 'mode', 'outcome'];

  enquiryForm: FormGroup;

  stats = { enquiry: 0, application: 0, assessment: 0, offer: 0, enrolled: 0 };

  constructor(
    private fb: FormBuilder,
    private admissionsService: AdmissionsService,
    private localStorage: LocalStorageService,
    private tenantService: TenantService,
    private snackBar: MatSnackBar
  ) {
    this.enquiryForm = this.fb.group({
      childFirstName: ['', Validators.required],
      childLastName: ['', Validators.required],
      seekingClass: ['10', Validators.required],
      academicYear: ['2025-2026', Validators.required],
      parentName: ['', Validators.required],
      parentPhone: ['', Validators.required],
      parentEmail: ['', Validators.email],
      notes: ['']
    });
  }

  ngOnInit(): void {
    this.isAdmin = this.localStorage.isAdmin();
    this.tenantService.getDisplayName().subscribe(n => {
      this.schoolName = (n && n.trim()) ? n.trim() : (this.localStorage.getSchoolDisplayName() || '');
    });
    this.reload();
  }

  reload(): void {
    this.loading = true;
    forkJoin({
      enquiries: this.admissionsService.listEnquiries(),
      interviews: this.admissionsService.listInterviews()
    }).subscribe(
      ({ enquiries, interviews }) => {
        this.enquiries = enquiries || [];
        this.enquiryData.data = this.enquiries;
        this.recomputeStats();
        this.interviews = interviews || [];
        this.interviewData.data = this.interviews;
        this.loading = false;
      },
      () => {
        this.enquiries = [];
        this.enquiryData.data = [];
        this.interviews = [];
        this.interviewData.data = [];
        this.loading = false;
      }
    );
  }

  private recomputeStats(): void {
    this.stats = {
      enquiry: this.enquiries.filter(e => e.stage === 'ENQUIRY').length,
      application: this.enquiries.filter(e => e.stage === 'APPLICATION').length,
      assessment: this.enquiries.filter(e => e.stage === 'ASSESSMENT').length,
      offer: this.enquiries.filter(e => e.stage === 'OFFERED').length,
      enrolled: this.enquiries.filter(e => e.stage === 'ENROLLED').length
    };
  }

  submitEnquiry(): void {
    if (this.enquiryForm.invalid) {
      return;
    }
    const v = this.enquiryForm.value;
    this.admissionsService
      .createEnquiry({
        childFirstName: v.childFirstName.trim(),
        childLastName: v.childLastName.trim(),
        seekingClass: v.seekingClass,
        academicYear: v.academicYear.trim(),
        parentName: v.parentName.trim(),
        parentPhone: v.parentPhone.trim(),
        parentEmail: (v.parentEmail || '').trim() || undefined,
        notes: (v.notes || '').trim() || undefined
      })
      .subscribe(
        () => {
          this.snackBar.open('Enquiry recorded', 'Dismiss', { duration: 3000 });
          this.enquiryForm.reset({
            childFirstName: '',
            childLastName: '',
            seekingClass: '10',
            academicYear: '2025-2026',
            parentName: '',
            parentPhone: '',
            parentEmail: '',
            notes: ''
          });
          this.reload();
        },
        () => {
          this.snackBar.open('Saved locally; API may be unavailable.', 'Dismiss', { duration: 4000 });
          this.reload();
        }
      );
  }

  changeStage(row: AdmissionEnquiry, stage: AdmissionStage): void {
    if (!this.isAdmin) {
      return;
    }
    this.admissionsService.updateEnquiryStage(row.id, stage).subscribe(
      () => {
        row.stage = stage;
        this.recomputeStats();
        this.snackBar.open('Stage updated', 'Dismiss', { duration: 2500 });
      },
      () => {
        row.stage = stage;
        this.recomputeStats();
      }
    );
  }

  stageLabel(s: AdmissionStage): string {
    return STAGE_LABEL[s] || s;
  }

  formatWhen(iso: string): string {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  }
}
